"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type JourneyStep = {
  number: string;
  title: string;
  description: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
};
const steps: JourneyStep[] = [{
  number: "01",
  title: "Morning Circle",
  description: "We begin our day with a warm welcome, sharing stories and setting our intentions. It is a time for every student to feel seen, heard, and ready to learn together.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/classroom_morning_circle.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/classroom_morning_circle.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/classroom_morning_circle.png-wvc-sizes",
  alt: "Students sitting in a circle on a colorful rug during morning meeting"
}, {
  number: "02",
  title: "Discovery Blocks",
  description: "Hands-on exploration takes center stage. From science experiments to building structures, we encourage curiosity and collaborative problem-solving in our dedicated activity zones.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/classroom_discovery_blocks.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/classroom_discovery_blocks.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/classroom_discovery_blocks.png-wvc-sizes",
  alt: "Children working together on a creative building project"
}, {
  number: "03",
  title: "Reading Nook",
  description: "A quiet sanctuary for imagination. Our cozy corner is filled with diverse stories, inviting students to dive into new worlds and develop a lifelong love for literature.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/classroom_reading_nook.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/classroom_reading_nook.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/classroom_reading_nook.png-wvc-sizes",
  alt: "A cozy reading corner with soft cushions and bookshelves"
}, {
  number: "04",
  title: "Artistic Expression",
  description: "Our walls are canvases for growth. Students express their unique perspectives through painting, drawing, and mixed media, celebrating the creative spirit of every child.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/classroom_art_wall.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/classroom_art_wall.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/classroom_art_wall.png-wvc-sizes",
  alt: "Colorful student artwork displayed on a classroom wall"
}, {
  number: "05",
  title: "Reflective Closing",
  description: "We wrap up our day by celebrating our achievements and tidying our space. It is a moment to practice gratitude and prepare for the adventures of tomorrow.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/classroom_closing_time.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/classroom_closing_time.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700640/img/classroom_closing_time.webp-wvc-sizes",
  alt: "Students tidying up the classroom at the end of the day"
}];
function StepRow({
  step,
  index
}: {
  step: JourneyStep;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div data-index={index} ref={ref} style={{
    transitionDelay: `${index * 200}ms`
  }} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      {}
      <div className="lg:hidden order-1">
        <div className="overflow-hidden rounded-2xl shadow-lg">
          <img src={step.image} data-wvc-srcset={step.srcset} data-wvc-sizes={step.sizes} alt={step.alt} loading="lazy" decoding="async" className="w-full aspect-[4/5] object-cover" />
        </div>
      </div>

      {}
      <div className="lg:col-span-3 order-2 lg:order-1">
        <div className="font-mono text-5xl lg:text-6xl text-[color:var(--chart-2)] leading-none tracking-wide">
          {step.number}
        </div>
        <div className="mt-4 h-px w-20 bg-border" aria-hidden="true" />
      </div>

      {}
      <div className="lg:col-span-5 order-3 lg:order-2">
        <h3 className="font-default text-2xl sm:text-3xl lg:text-[32px] leading-tight text-foreground font-semibold">
          {step.title}
        </h3>
        <p className="mt-5 text-base lg:text-[17px] leading-relaxed text-muted-foreground max-w-md">
          {step.description}
        </p>
      </div>

      {}
      <div className="hidden lg:block lg:col-span-4 order-4 lg:order-3">
        <div className="overflow-hidden rounded-2xl shadow-lg">
          <img src={step.image} data-wvc-srcset={step.srcset} data-wvc-sizes={step.sizes} alt={step.alt} loading="lazy" decoding="async" className="w-full aspect-[4/5] object-cover" />
        </div>
      </div>
    </div>;
}
export default function OurDailyRhythm() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="2982" id="our-daily-rhythm" className="bg-background text-foreground py-24 sm:py-32 lg:py-40">
      <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-10 lg:px-16">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-6 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-12">
            <p className="font-mono text-xs sm:text-sm tracking-widest text-[color:var(--chart-1)] uppercase">DAILY LIFE</p>
          </div>
          <div className="lg:col-span-8">
            <h2 className="font-default font-bold text-foreground leading-[1.02] tracking-tight mt-8 sm:mt-10" style={{
            fontSize: "clamp(40px, 6vw, 88px)"
          }}>
              A day in the life of Class 2A.
            </h2>
          </div>
        </div>

        {}
        <div className="mt-20 lg:mt-[120px]">
          {steps.map((step, i) => <div key={step.number} data-index={i}>
              <StepRow step={step} index={i} />
              {i < steps.length - 1 && <div className="my-16 lg:my-20 h-px w-full bg-border" aria-hidden="true" />}
            </div>)}
        </div>
      </div>
    </section>;
}